import React, { ReactNode, useState, useCallback } from 'react';
import styled from 'styled-components';
import GridLayout, { Layout } from 'react-grid-layout';
// CSS imports removed to avoid build errors
// These styles should be included in the final application

// Dashboard item interface
export interface DashboardItem {
  /**
   * Unique identifier for the item
   */
  id: string | number;
  /**
   * X position in the grid
   */
  x: number;
  /**
   * Y position in the grid
   */
  y: number;
  /**
   * Width in grid units
   */
  width: number;
  /**
   * Height in grid units
   */
  height: number;
  /**
   * Content to render in the item
   */
  content: ReactNode;
  /**
   * Whether the item is resizable
   */
  isResizable?: boolean;
  /**
   * Whether the item is draggable
   */
  isDraggable?: boolean;
}

// Dashboard props interface
export interface DashboardProps {
  /**
   * Dashboard title
   */
  title?: string;
  /**
   * Dashboard description
   */
  description?: string;
  /**
   * Items to display in the dashboard
   */
  items: DashboardItem[];
  /**
   * Number of columns in the grid
   */
  columns?: number;
  /**
   * Row height in pixels
   */
  rowHeight?: number;
  /**
   * Gap between items in pixels
   */
  gap?: number;
  /**
   * Whether the dashboard is editable
   */
  isEditable?: boolean;
  /**
   * Callback when items are moved or resized
   */
  onLayoutChange?: (items: DashboardItem[]) => void;
  /**
   * Additional class name
   */
  className?: string;
}

// Styled components
const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const DashboardHeader = styled.div`
  margin-bottom: 1rem;
`;

const DashboardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${(props: any) => props.theme?.colors?.neutral?.[900] || '#212121'};
  margin: 0 0 0.5rem 0;
`;

const DashboardDescription = styled.p`
  font-size: 0.875rem;
  color: ${(props: any) => props.theme?.colors?.neutral?.[600] || '#757575'};
  margin: 0;
`;

const StyledGridLayout = styled(GridLayout)`
  width: 100%;
  
  .react-grid-item {
    transition: all 200ms ease;
    transition-property: left, top, width, height;
    
    &.react-grid-placeholder {
      background: ${(props: any) => props.theme?.colors?.primary?.[100] || '#BAE7FF'};
      opacity: 0.2;
      border-radius: 4px;
    }
    
    &.react-draggable-dragging {
      z-index: 100;
      box-shadow: ${(props: any) => props.theme.shadows.lg};
    }
    
    &.react-resizable-resizing {
      z-index: 100;
    }
  }
  
  .react-resizable-handle {
    position: absolute;
    width: 20px;
    height: 20px;
    bottom: 0;
    right: 0;
    cursor: se-resize;
    
    &::after {
      content: "";
      position: absolute;
      right: 3px;
      bottom: 3px;
      width: 5px;
      height: 5px;
      border-right: 2px solid ${(props: any) => props.theme?.colors?.primary?.[500] || '#1890FF'};
      border-bottom: 2px solid ${(props: any) => props.theme?.colors?.primary?.[500] || '#1890FF'};
    }
  }
`;

const DashboardItemContent = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;
  border-radius: 4px;
`;

/**
 * Dashboard component for displaying metrics, charts, and other content in a grid layout
 * Implements drag-and-drop functionality using react-grid-layout
 */
const Dashboard: React.FC<DashboardProps> = ({
  title,
  description,
  items,
  columns = 4,
  rowHeight = 100,
  gap = 16,
  isEditable = false,
  onLayoutChange,
  className,
}) => {
  // Convert items to layout format for react-grid-layout
  const initialLayout: Layout[] = items.map(item => ({
    i: item.id.toString(),
    x: item.x,
    y: item.y,
    w: item.width,
    h: item.height,
    isDraggable: isEditable && (item.isDraggable !== false),
    isResizable: isEditable && (item.isResizable !== false),
  }));
  
  const [layout, setLayout] = useState<Layout[]>(initialLayout);
  
  // Handle layout change
  const handleLayoutChange = useCallback((newLayout: Layout[]) => {
    setLayout(newLayout);
    
    if (onLayoutChange) {
      // Convert layout back to DashboardItem format
      const updatedItems = items.map(item => {
        const layoutItem = newLayout.find(l => l.i === item.id.toString());
        if (layoutItem) {
          return {
            ...item,
            x: layoutItem.x,
            y: layoutItem.y,
            width: layoutItem.w,
            height: layoutItem.h,
          };
        }
        return item;
      });
      
      onLayoutChange(updatedItems);
    }
  }, [items, onLayoutChange]);
  
  // Calculate margin based on gap
  const margin: [number, number] = [gap / 2, gap / 2];
  
  return (
    <DashboardContainer className={className}>
      {(title || description) && (
        <DashboardHeader>
          {title && <DashboardTitle>{title}</DashboardTitle>}
          {description && <DashboardDescription>{description}</DashboardDescription>}
        </DashboardHeader>
      )}
      
      <StyledGridLayout
        className="layout"
        layout={layout}
        cols={columns}
        rowHeight={rowHeight}
        width={1200} // This will be overridden by the parent container width
        autoSize={true}
        margin={margin}
        isDraggable={isEditable}
        isResizable={isEditable}
        onLayoutChange={handleLayoutChange}
        draggableHandle=".drag-handle"
        compactType="vertical"
        useCSSTransforms={true}
      >
        {items.map(item => (
          <div key={item.id.toString()}>
            <DashboardItemContent>
              {item.content}
            </DashboardItemContent>
          </div>
        ))}
      </StyledGridLayout>
    </DashboardContainer>
  );
};

export default Dashboard;